import { Formik } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import React from 'react';
import * as Yup from 'yup'; // Add this import
import Form, { FormElement } from '@dashboard/components/Form';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import OptionList from '@dashboard/components/Formik/components/OptionList';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import usePage from '@dashboard/hooks/usePage';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';

const EventDiscountsForm = () => {
  const { page, updatePage } = usePage();

  const ticketTypes = page.ticketGroups.flatMap(tg => tg.ticketTypes);

  // Add this validation schema
  const validationSchema = Yup.object().shape({
    discounts: Yup.array().of(
      Yup.object().shape({
        quantityCondition: Yup.string().required('Quantity condition is required'),
        ticketTypes: Yup.array().when('quantityCondition', {
          is: 'ticketTypeQuantity',
          then: Yup.array().min(1, 'Select at least one ticket type'),
          otherwise: Yup.array(),
        }),
        minQuantity: Yup.number().required('Minimum quantity is required'),
        maxQuantity: Yup.number().nullable(),
        discountType: Yup.string().required('Discount type is required'),
        amountOff: Yup.string().when('discountType', {
          is: 'amountOff',
          then: Yup.string().required('Discount amount is required'),
          otherwise: Yup.string().nullable(),
        }),
        percentOff: Yup.number().when('discountType', {
          is: 'percentOff',
          then: Yup.number().required('Discount percentage is required'),
          otherwise: Yup.number().nullable(),
        }),
      }),
    ),
  });

  return (
    <Formik
      validationSchema={validationSchema} // Add the validation schema here
      initialValues={{
        discounts:
          page?.eventDetails?.discounts?.map(discount => ({
            ...discount,
            key: uniqueId(),
            quantityCondition: discount.quantityCondition || 'totalQuantity',
            ticketTypes: discount.ticketTypes || [],
            discountType: discount.amountOff ? 'amountOff' : 'percentOff',
            amountOff: discount.amountOff
              ? formatPriceForInput({
                  amount: discount.amountOff,
                  currency: page.eventDetails?.currency,
                  style: 'decimal',
                })
              : '',
          })) || [],
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus(undefined);

        const body = {
          eventDetails: {
            ...page.eventDetails,
            discounts: values.discounts.map(discount => ({
              ...discount,
              ticketTypes:
                discount.quantityCondition === 'ticketTypeQuantity' ? discount.ticketTypes : [],
              amountOff:
                discount.discountType === 'amountOff'
                  ? formatPriceInput(discount.amountOff, page.eventDetails?.currency)
                  : null,
              percentOff: discount.discountType === 'percentOff' ? discount.percentOff : null,
            })),
          },
        };

        const { data, error } = await updatePage.mutateAsync(body);

        if (data) {
          actions.setStatus('');
          actions.resetForm({
            values: {
              discounts: data.eventDetails.discounts.map(discount => ({
                ...discount,
                key: uniqueId(),
                discountType: discount.amountOff ? 'amountOff' : 'percentOff',
                amountOff: discount.amountOff
                  ? formatPriceForInput({
                      amount: discount.amountOff,
                      currency: page.eventDetails?.currency,
                      style: 'decimal',
                    })
                  : '',
                percentOff: discount.percentOff || '',
              })),
            },
          });
        } else {
          const { status, errors } = transformApiErrorToFormik(error);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }
        }
      }}
    >
      {({ values, status, dirty, handleSubmit, setFieldValue }) => (
        <Form onSubmit={handleSubmit}>
          <FormElement>
            <OptionList
              name="discounts"
              options={values.discounts}
              onChange={options => setFieldValue('discounts', options)}
              optionShape={{
                minQuantity: '',
                maxQuantity: '',
                discountType: 'amountOff',
                amountOff: '',
                percentOff: '',
                quantityCondition: 'totalQuantity',
                ticketTypes: [],
              }}
              addOptionLabel="+ Add discount rule"
              deleteOptionLabel="Delete discount rule?"
              fields={({ values: fieldValues }) => {
                return [
                  {
                    name: 'quantityCondition',
                    label: 'Applies to',
                    tooltip:
                      'Apply this discount based on total selected quantity or quantity per ticket.',
                    type: 'radioCard',
                    options: [
                      { label: 'All tickets', value: 'totalQuantity' },
                      { label: 'Per ticket', value: 'ticketTypeQuantity' },
                    ],
                    required: true,
                  },
                  {
                    name: 'ticketTypes',
                    label: 'Ticket types',
                    type: 'listbox',
                    multiple: true,
                    visible: fieldValues.quantityCondition === 'ticketTypeQuantity',
                    placeholder: 'Select ticket types',
                    options: ticketTypes.map(ticketType => ({
                      name: ticketType.name,
                      value: ticketType._id,
                    })),
                  },
                  {
                    type: 'columns',
                    columns: 2,
                    gap: 'sm',
                    fields: [
                      {
                        name: 'minQuantity',
                        label: 'Min. quantity',
                        placeholder: 'Min. quantity',
                        type: 'number',
                        required: true,
                      },
                      {
                        name: 'maxQuantity',
                        label: 'Max. quantity',
                        placeholder: 'Max. quantity',
                        type: 'number',
                        optional: true,
                      },
                      {
                        name: 'discountType',
                        label: 'Discount type',
                        type: 'select',
                        options: [
                          { label: 'Fixed amount', value: 'amountOff' },
                          { label: 'Percentage', value: 'percentOff' },
                        ],
                        required: true,
                      },
                      {
                        name: 'amountOff',
                        label: 'Discount amount',
                        placeholder: 'Discount amount',
                        type: 'string',
                        visible: fieldValues.discountType === 'amountOff',
                      },
                      {
                        name: 'percentOff',
                        label: 'Discount %',
                        placeholder: 'Discount %',
                        type: 'number',
                        visible: fieldValues.discountType === 'percentOff',
                      },
                    ],
                  },
                ];
              }}
            />
          </FormElement>

          <FormElement>{status && status}</FormElement>

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>
            <FormSubmit.State visible={!!dirty} />
          </FormSubmit>
        </Form>
      )}
    </Formik>
  );
};

export default EventDiscountsForm;
